import { useState, useEffect } from "react";
import { useGlobal } from "../context/ContenedorGlobal";
import "../styles/Formularios.css";
import "../styles/TablaReportes.css";
import InputGenerico from "./InputGenerico";
import InputSelectGenerico from "./InputSelectGenerico";
import InputSelectEstados from "./InputSelectEstados";
import InputBienesAutocomplete from "./InputBienesAutocomplete";
import InputUsuarioAutocomplete from "./InputUsuarioAutocomplete";
import { useApiFetch } from "../utils/apiFetch";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../assets/logo_sict.png";

function ReporteSolicitudes({ filtros, filtroPrincipal, setVista }) {

    const { logout } = useGlobal();
    const apiFetch = useApiFetch();
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");
    const [resultados, setResultados] = useState([]);
    const [vistaLocal, setVistaLocal] = useState("formulario"); // Para separar el formulario de los resultados

    const [folio, setFolio] = useState(null);
    const [folioInicial, setFolioInicial] = useState(null);
    const [folioFinal, setFolioFinal] = useState(null);
    const [fechaCreacion, setFechaCreacion] = useState(null);
    const [fechaTermino, setFechaTermino] = useState(null);
    const [fechaCierre, setFechaCierre] = useState(null);
    const [idTecnico, setIdTecnico] = useState(null);
    const [idCategoria, setIdCategoria] = useState(null);
    const [idEstado, setIdEstado] = useState(null);
    const [idFalla, setIdFalla] = useState(null);
    const [serieBien, setSerieBien] = useState(null);
    const [usuario, setUsuario] = useState(null);
    const [operador, setOperador] = useState(null);
    const [palabra, setPalabra] = useState(null);
    const [fechaInicial, setFechaInicial] = useState(null);
    const [fechaFinal, setFechaFinal] = useState(null);

    const [paginaActiva, setPaginaActiva] = useState(0);
    const registrosPorPagina = 8;

    // Función para realizar validaciones de filtros seleccionados y los valores de sus variables
    function validarCampos(filtros, {
        folio,
        folioInicial,
        folioFinal,
        fechaCreacion,
        fechaTermino,
        fechaCierre,
        idTecnico,
        idCategoria,
        idEstado,
        idFalla,
        serieBien,
        usuario,
        operador,
        palabra,
        fechaInicial,
        fechaFinal,
    }) {
        // Folio único
        if (filtros.opcFolio && !folio) {
            return { valido: false, mensaje: "Debes ingresar el folio." };
        }

        // Rango de folios
        if (filtros.opcRangoFolios) {
            if (!folioInicial || !folioFinal) {
                return { valido: false, mensaje: "Debes ingresar ambos folios." };
            }
            if (parseInt(folioFinal) <= parseInt(folioInicial)) {
                return { valido: false, mensaje: "El folio final debe ser mayor al inicial." };
            }
        }

        // Fechas individuales
        if (filtros.opcFCreacion && !fechaCreacion) {
            return { valido: false, mensaje: "Debes ingresar la fecha de creación." };
        }
        if (filtros.opcFtermino && !fechaTermino) {
            return { valido: false, mensaje: "Debes ingresar la fecha de término." };
        }
        if (filtros.opcFcierre && !fechaCierre) {
            return { valido: false, mensaje: "Debes ingresar la fecha de cierre." };
        }

        // Rango de fechas
        if (filtros.opcRangoFechas) {
            if (!fechaInicial || !fechaFinal) {
                return { valido: false, mensaje: "Debes ingresar ambas fechas." };
            }
            if (new Date(fechaFinal) <= new Date(fechaInicial)) {
                return { valido: false, mensaje: "La fecha final debe ser mayor a la inicial." };
            }
        }

        // Selects y autocompletes
        if (filtros.opcTecnico && !idTecnico) {
            return { valido: false, mensaje: "Debes seleccionar un técnico." };
        }
        if (filtros.opcCategoria && !idCategoria) {
            return { valido: false, mensaje: "Debes seleccionar una categoría." };
        }
        if (filtros.opcEstado && !idEstado) {
            return { valido: false, mensaje: "Debes seleccionar un estado." };
        }
        if (filtros.opcFalla && !idFalla) {
            return { valido: false, mensaje: "Debes seleccionar una falla." };
        }
        if (filtros.opcSerie && !serieBien) {
            return { valido: false, mensaje: "Debes seleccionar la serie del bien." };
        }
        if (filtros.opcUsuario && !usuario) {
            return { valido: false, mensaje: "Debes seleccionar un usuario." };
        }
        if (filtros.opcOperador && !operador) {
            return { valido: false, mensaje: "Debes seleccionar un operador." };
        }
        if (filtros.opcPalabra && !palabra) {
            return { valido: false, mensaje: "Debes ingresar una palabra clave." };
        }

        // Caso especial si la seleccion fue todas ya que en automatico es true.
        if (filtros.opcTodas) {
            return { valido: true, mensaje: "" };
        }

        // Si pasa todas las validaciones
        return { valido: true, mensaje: "" };
    }

    // Función para evaluar si el filtroPrincipal es opcTodas para generar el reporte de inmediato
    useEffect(() => {
        if (filtroPrincipal === "opcTodas" && filtros.opcTodas) {
            // Ejecutar inmediatamente el reporte

            handleGenerarReporte();
        }
    }, [filtroPrincipal, filtros]);

    // Funcion para limpiar los mensajes
    useEffect(() => {
        if (success || error) {
            const timer = setTimeout(() => {
                setSuccess("");
                setError("");
            }, 3000);

            return () => clearTimeout(timer); // Limpia el timer si cambia el estado
        }
    }, [success, error]);

    // Función regresar a la vista anterior
    const handleRegresar = () => {
        setVista("solicitudes"); // Vuelve a la vista de filtros
        setSuccess("");
        setError("");
        setResultados([]);
    };

    // Función para conectar y enviar los datos a la api
    const handleGenerarReporte = async () => {
        const valores = {
            folio,
            folioInicial,
            folioFinal,
            fechaCreacion,
            fechaTermino,
            fechaCierre,
            idTecnico,
            idCategoria,
            idEstado,
            idFalla,
            serieBien,
            usuario,
            operador,
            palabra,
            fechaInicial,
            fechaFinal,
            filtroPrincipal
        };

        const resultado = validarCampos(filtros, valores);

        if (!resultado.valido) {
            setError(resultado.mensaje);
            setSuccess("");
            return;
        }

        try {
            const data = await apiFetch("reporte_solicitudes.php", {
                method: "POST",
                body: JSON.stringify(valores),
                headers: { "Content-Type": "application/json" }
            }, logout);

            if (data.status === "ok" && data.data.length > 0) {
                setResultados(data.data || []); // Se guardan los resultados
                setSuccess("Reporte generado correctamente.");
                setVistaLocal("resultados");
                setError("");
            } else if (data.status === "no_results") {
                setError("No se encontraron registros que cumplan con los filtros.");
                setSuccess("");
            } else {
                setError("Error al generar reporte: " + (data.message || "Error desconocido."));
                setSuccess("");
            }
        } catch (err) {
            setError("Error de conexión: " + err.message);
            setSuccess("");
        }
    };

    // Función para regresar al formulario
    const handleRegresarFormulario = () => {
        setVistaLocal("formulario");
        setSuccess("");
        setError("");
    };

    // Limpiar los inpus para facilitar el reingreso de datos
    const handleLimpiar = () => {
        setFolio("");
        setFolioInicial("");
        setFolioFinal("");
        setFechaCreacion(null);
        setFechaTermino(null);
        setFechaCierre(null);
        setIdTecnico(null);
        setIdCategoria(null);
        setIdEstado(null);
        setIdFalla(null);
        setSerieBien(null);
        setUsuario(null);
        setOperador(null);
        setPalabra("");
        setFechaInicial(null);
        setFechaFinal(null);

        setResultados([]);
        setSuccess("");
        setError("");
    };

    // Diccionario de cabeceras para la lista que se muestra al usuario y sea mas entendible
    const diccionarioCabeceras = {
        folio: "Folio",
        fecha_crea: "Fecha de creación",
        fecha_cierre: "Fecha de cierre",
        fecha_term: "Fecha de término",
        id_categoria: "Id Categoría",
        id_tecnico: "Id Técnico",
        id_estado: "Id Estado",
        id_falla: "Id Falla",
        id_usuario: "Id Usuario",
        id_uso: "Id Operador",
        serie_bien: "Serie",
        notas_solucion: "Notas y/o solución",
        nombreCategoria: "Categoría",
        nombreTecnico: "Técnico",
        nombreUsuario: "Reportó",
        nombreOperador: "Operador",
        nombreEstado: "Estado",
        nombreFalla: "Falla"
    };

    // Función para mostrar solo ciertas columnas y no saturar la pantalla con exceso de información
    const columnasVisibles = [
        "folio",
        "fecha_crea",
        "nombreCategoria",
        "nombreTecnico",
        "nombreUsuario",
        "nombreEstado",
        "nombreFalla"
    ];

    // Funcion para exportar en CSV (todas las columnas de la consulta, no solo de la tabla solicitudes)
    const exportCSV = () => {
        if (!resultados.length) return;

        const columnas = Object.keys(resultados[0]); // Todas las columnas
        const filas = resultados.map(fila => columnas.map(col => fila[col]));

        const csvRows = [];
        csvRows.push(columnas.join(",")); // Encabezados (campos de la consulta )
        filas.forEach(row => csvRows.push(row.join(",")));

        const csvString = csvRows.join("\n");
        const BOM = "\uFEFF"; // UTF-8 BOM
        const blob = new Blob([BOM + csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "reporte_solicitudes.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Función para exportar en PDF (solo lo que se muestra en la tabla)
    const exportPDF = () => {
        if (!resultados.length) return;

        const doc = new jsPDF();

        // Logo institucional
        doc.addImage(logo, "PNG", 10, 10, 30, 15);

        // Encabezado
        doc.setFontSize(14);
        doc.text("Secretaría de Infraestructura, Comunicaciones y Transportes", 50, 20);
        doc.setFontSize(12);
        doc.text("Reporte de Solicitudes", 50, 30);

        doc.setLineWidth(0.5);
        doc.line(10, 40, 200, 40);

        // Tabla con columnas visibles
        autoTable(doc, {
            startY: 50,
            head: [columnasVisibles.map(col => diccionarioCabeceras[col] || col)],
            body: resultados.map(fila =>
                columnasVisibles.map(col => fila[col])
            )
        });

        // Pie de página
        doc.setFontSize(10);
        doc.text("© 2026 SIGBI-712. Todos los derechos reservados.", 10, 290);

        doc.save("reporte_solicitudes.pdf");
    };


    return (
        <div className="sesion-form">

            {success && <div className="success">{success}</div>}
            {error && <div className="error">{error}</div>}

            {vistaLocal === "formulario" && (
                <>
                    <h3>Ingrese los datos para generar el reporte</h3>

                    {filtros.opcFolio && (
                        <div>
                            <InputGenerico
                                value={folio}
                                setValue={setFolio}
                                label="Ingrese el folio"
                                maxLength={4}
                                allowedChars="0-9"
                                placeholder="123"
                                title="Ingrese solo dígitos"
                            />
                        </div>
                    )}

                    {filtros.opcRangoFolios && (
                        <div>
                            <InputGenerico
                                value={folioInicial}
                                setValue={setFolioInicial}
                                label="Ingrese el folio inicial"
                                maxLength={4}
                                allowedChars="0-9"
                                placeholder="123"
                                title="Ingrese solo dígitos"
                            />

                            <InputGenerico
                                value={folioFinal}
                                setValue={setFolioFinal}
                                label="Ingrese el folio final"
                                maxLength={4}
                                allowedChars="0-9"
                                placeholder="123"
                                title="Ingrese solo dígitos"
                            />
                        </div>
                    )}

                    {filtros.opcFcreacion && (
                        <div>
                            <label>Fecha de creación:</label>
                            <input
                                type="date"
                                value={fechaCreacion}
                                onChange={(e) => setFechaCreacion(e.target.value)}
                            />
                        </div>
                    )}

                    {filtros.opcFtermino && (
                        <div>
                            <label>Fecha de término:</label>
                            <input
                                type="date"
                                value={fechaTermino}
                                onChange={(e) => setFechaTermino(e.target.value)}
                            />
                        </div>
                    )}

                    {filtros.opcFcierre && (
                        <div>
                            <label>Fecha de cierre:</label>
                            <input
                                type="date"
                                value={fechaCierre}
                                onChange={(e) => setFechaCierre(e.target.value)}
                            />
                        </div>
                    )}

                    {filtros.opcTecnico && (
                        <div>
                            <InputSelectGenerico
                                idSeleccionado={idTecnico}
                                setIdSeleccionado={setIdTecnico}
                                label="Seleccione un Técnico"
                                apiUrl={`consultar_tecnicos2.php`}
                                valueField="id_tecnico"
                                displayField={(o) => o.nombre_completo}
                                readOnly={false}
                                showDefaultOption={true}
                                defaultOptionText="Seleccione un técnico"
                                dataField="tecnicos"
                            />
                        </div>
                    )}

                    {filtros.opcCategoria && (
                        <div>
                            <InputSelectGenerico
                                idSeleccionado={idCategoria}
                                setIdSeleccionado={setIdCategoria}
                                label="Seleccione una categoría"
                                apiUrl={`consultar_categorias.php`}
                                valueField="id_categoria"
                                displayField="categoria"
                                readOnly={false}
                                showDefaultOption={true}
                                defaultOptionText="Seleccione una categoría"
                            />
                        </div>
                    )}

                    {filtros.opcEstado && (
                        <div>
                            <InputSelectEstados
                                idEstado={idEstado}
                                setIdEstado={setIdEstado}
                                estadoActualText=""
                                idEntidad={4}
                                label="Seleccione un estado de la solicitud"
                            />
                        </div>
                    )}

                    {filtros.opcFalla && (
                        <div>
                            <InputSelectGenerico
                                idSeleccionado={idFalla}
                                setIdSeleccionado={setIdFalla}
                                label="Seleccione una falla"
                                apiUrl={`consultar_fallas.php`}
                                valueField="id_falla"
                                displayField="falla"
                                readOnly={false}
                                showDefaultOption={true}
                                defaultOptionText="Seleccione una falla"
                            />
                        </div>
                    )}

                    {filtros.opcSerie && (
                        <div>
                            <InputBienesAutocomplete
                                idSeleccionado={serieBien}
                                setIdSeleccionado={setSerieBien}
                                label="Seleccione la serie del bien"
                                readOnly={false}
                            />
                        </div>
                    )}

                    {filtros.opcUsuario && (
                        <div>
                            <InputUsuarioAutocomplete
                                idSeleccionado={usuario}
                                setIdSeleccionado={setUsuario}
                                label="Seleccione al usuario que reportó"
                                placeholder="Ingrese apellido paterno"
                                title="Seleccione al usuario que reportó la solicitud"
                                readOnly={false}
                            />
                        </div>
                    )}

                    {filtros.opcOperador && (
                        <div>
                            <InputUsuarioAutocomplete
                                idSeleccionado={operador}
                                setIdSeleccionado={setOperador}
                                label="Seleccione al usuario operador"
                                placeholder="Ingrese apellido paterno"
                                title="Seleccione al usuario operador"
                                readOnly={false}
                            />
                        </div>
                    )}

                    {filtros.opcPalabra && (
                        <div>
                            <InputGenerico
                                value={palabra}
                                setValue={setPalabra}
                                label="Ingrese palabra clave"
                                maxLength={30}
                                allowedChars="a-zA-Z0-9ÁÉÍÓÚáéíóúñÑ "
                                placeholder="quemado"
                                title="Ingrese solo letras, números y espacios"
                            />
                        </div>
                    )}

                    {filtros.opcRangoFechas && (
                        <div>
                            <label>Fecha inicial:</label>
                            <input
                                type="date"
                                value={fechaInicial}
                                onChange={(e) => setFechaInicial(e.target.value)}
                            />

                            <label>Fecha final:</label>
                            <input
                                type="date"
                                value={fechaFinal}
                                onChange={(e) => setFechaFinal(e.target.value)}
                            />
                        </div>
                    )}

                    <div className="form-buttons">

                        <button onClick={handleGenerarReporte}>
                            Generar<br />reporte
                        </button>

                        <button onClick={handleLimpiar}>
                            Limpiar<br />datos
                        </button>

                        <button onClick={handleRegresar}>
                            Regresar
                        </button>

                    </div>
                </>
            )}

            {vistaLocal === "resultados" && (
                <>
                    <h3>Resultados del reporte</h3>

                    {resultados.length > 0 && (
                        <>
                            <table className="tabla-estandar">
                                <thead>
                                    <tr>
                                        {columnasVisibles.map((col) => (
                                            <th key={col}>{diccionarioCabeceras[col] || col}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {resultados
                                        .slice(paginaActiva * registrosPorPagina, paginaActiva * registrosPorPagina + registrosPorPagina)
                                        .map((fila, idx) => (
                                            <tr key={idx}>
                                                {columnasVisibles.map((col) => (
                                                    <td key={col} data-label={diccionarioCabeceras[col] || col}>
                                                        {fila[col]}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                </tbody>
                            </table>

                            {/* Controles de paginación */}
                            <div className="tabs-header">
                                {Array.from({ length: Math.ceil(resultados.length / registrosPorPagina) }).map((_, index) => (
                                    <button
                                        key={index}
                                        className={paginaActiva === index ? "tab-activa" : ""}
                                        onMouseEnter={() => setPaginaActiva(index)}
                                    >
                                        {index + 1}
                                    </button>
                                ))}
                            </div>

                            <div className="form-buttons">
                                {filtroPrincipal === "opcTodas" ? (
                                    <button onClick={handleRegresar}>Regresar<br /></button>
                                ) : (
                                    <button onClick={handleRegresarFormulario}>Regresar<br />al formulario</button>
                                )}
                                <button onClick={exportCSV}>Exportar<br />en CSV</button>
                                <button onClick={exportPDF}>Exportar<br />en PDF</button>
                            </div>
                        </>
                    )}
                </>
            )}


        </div>

    );

}

export default ReporteSolicitudes;


